
/**
 * Version information utilities for the PWA
 * Handles retrieving and storing version data
 */

// Define version info interface
export interface VersionInfo {
  buildTimestamp: string;
  version: string;
  productionUrl: string;
}

// Global version info
let currentVersionInfo: VersionInfo | null = null;

// Get current version info
export const getVersionInfo = (): VersionInfo => {
  // If version info is not loaded yet, try loading it synchronously
  // Ensure we NEVER return null, always a valid version object
  if (!currentVersionInfo) {
    try {
      const cachedVersion = localStorage.getItem('app_version');
      const cachedVersionNumber = localStorage.getItem('app_version_number') || '1.0.0';
      
      if (cachedVersion) {
        const fallback: VersionInfo = {
          version: cachedVersionNumber,
          buildTimestamp: cachedVersion,
          productionUrl: 'https://vote.gameon.futbol'
        };
        console.log('[Version] Falling back to cached version:', fallback);
        return fallback;
      }
      // If not cached, use the default version.
      const defaultVersion: VersionInfo = {
        version: '1.0.0',
        buildTimestamp: new Date().toISOString(),
        productionUrl: 'https://vote.gameon.futbol'
      };
      console.warn('[Version] Using default version info:', defaultVersion);
      return defaultVersion;
    } catch (e) {
      console.error('[Version] Error getting version info:', e);
      // Return a safe default if there's an error
      return {
        version: '0.0.0',
        buildTimestamp: 'unknown',
        productionUrl: 'https://vote.gameon.futbol'
      };
    }
  }

  // Defensive: always patch all fields with safe fallback values
  const info = currentVersionInfo as VersionInfo;
  const safeVersion: VersionInfo = {
    version: info.version || '0.0.0',
    buildTimestamp: info.buildTimestamp || 'unknown',
    productionUrl: info.productionUrl || 'https://vote.gameon.futbol'
  };
  
  // Log current version info for debugging
  if (safeVersion.buildTimestamp === 'unknown' || safeVersion.version === '0.0.0') {
    console.warn('[Version] Warning: Using possibly stale or default version info:', safeVersion);
  } else {
    console.log('[Version] Using loaded version info:', safeVersion);
  }
  return safeVersion;
};

// Update the global version info
export const setVersionInfo = (versionInfo: VersionInfo): void => {
  currentVersionInfo = versionInfo;
  console.log('[Version] Updated version info:', versionInfo);
};
